import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { prepareNotificationText } from './prepareNotificationText';
import NotificationLink from './NotificationLink';
import type { ISystemLink, ISystemLinkContext } from '@/utils/resolveSystemLink';
import type { loadSystemLinkTitle } from '@services/loadSystemLinkTitle';
import { getSystemLinkContext } from '@molecules/SystemLink/systemLinkContext';
import { useSystemLinkTitle } from '@molecules/SystemLink/useSystemLinkTitle';
import './NotificationText.scss';

interface INotificationTextProps {
    text?: string;
    className?: string;
    linkContext?: ISystemLinkContext;
    /** Без него названия идут через общий кэш с пакетной загрузкой документов. */
    loadTitle?: typeof loadSystemLinkTitle;
}

/** Название сущности приходит из общего кэша: повторы ссылки в тексте и в соседних уведомлениях
 *  делят один запрос, а документы собираются в пакеты. */
const NotificationTextLink = ({ link, loadTitle }: { link: ISystemLink; loadTitle?: typeof loadSystemLinkTitle }) => {
    const title = useSystemLinkTitle(link.lookup, loadTitle);
    return <NotificationLink link={title ? { ...link, title } : link} />;
};

const NotificationText = ({ text = '', className, linkContext, loadTitle }: INotificationTextProps) => {
    const root = useRef<HTMLDivElement>(null);
    const [slots, setSlots] = useState<Element[]>([]);
    const prepared = useMemo(
        () => prepareNotificationText(text, linkContext ?? getSystemLinkContext()),
        [text, linkContext],
    );
    // Стабильный объект не даёт React повторно записать innerHTML поверх уже смонтированных порталов.
    const markup = useMemo(() => ({ __html: prepared.html }), [prepared.html]);

    // Сохраняем HTML шаблона (включая таблицы и стили); React управляет только заменёнными ссылками.
    useLayoutEffect(() => {
        setSlots(Array.from(root.current?.querySelectorAll('[data-notification-link]') ?? []));
    }, [prepared]);

    return (
        <>
            <div ref={root} className={clsx('notification-text', className)} dangerouslySetInnerHTML={markup} />
            {slots.map(
                (slot, index) =>
                    prepared.links[index] &&
                    createPortal(
                        <NotificationTextLink link={prepared.links[index]} loadTitle={loadTitle} />,
                        slot,
                        `${index}:${prepared.links[index].href}`,
                    ),
            )}
        </>
    );
};

export default NotificationText;
